import './App.css';
import { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import AboutMe from './components/AboutMe';
import NotFinished from './components/NotFinished';
import AwsApplications from './components/AwsApplications';
import PantryPal from './components/PantryPal';

function ScrollRestoration({ children }) {
  const location = useLocation();

  useLayoutEffect(() => {
    const pos = sessionStorage.getItem('scroll-' + location.pathname);
    if (pos) {
      const { x, y } = JSON.parse(pos);
      window.scrollTo(x, y);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem(
        'scroll-' + location.pathname,
        JSON.stringify({ x: window.scrollX, y: window.scrollY })
      );
    };
    window.addEventListener('beforeunload', saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener('beforeunload', saveScroll);
    };
  }, [location.pathname]);

  return children;
}

function ExperienceCard({ title, company, period, description, bulletPoints = [], skills = [], logoUrl }) {
  return (
    <div className="experience-card">
      <div className="experience-header">
        <div className="company-info">
          {logoUrl && (
            <div className="company-logo-container">
              <img src={logoUrl} alt={`${company} logo`} className="company-logo" />
            </div>
          )}
          <div className="company-details">
            <h3 className="job-title">{title}</h3>
            <span className="company">{company}</span>
            <span className="period">{period}</span>
          </div>
        </div>
      </div>
      <p className="experience-description">{description}</p>
      {bulletPoints.length > 0 && (
        <div className="contributions-container">
          <h4 className="contributions-title">Key Contributions:</h4>
          <ul className="experience-bullets">
            {bulletPoints.map((point, index) => (
              <li key={index} className="experience-bullet-item">{point}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="skills-container">
        {skills.map((skill, index) => (
          <span key={index} className="skill-tag">{skill}</span>
        ))}
      </div>
    </div>
  );
}

function EducationCard({ degree, school, period, description, courses, logoUrl }) {
  const awards = description.split('\n');

  return (
    <div className="education-card">
      <div className="education-header">
        <div className="school-info">
          {logoUrl && (
            <div className="school-logo-container">
              <img src={logoUrl} alt={`${school} logo`} className="school-logo" />
            </div>
          )}
          <div className="school-details">
            <h3 className="degree-title">{degree}</h3>
            <span className="school">{school}</span>
            <span className="period">{period}</span>
          </div>
        </div>
      </div>
      <div className="awards-container">
        <h4 className="awards-title">Awards:</h4>
        <ul className="awards-list">
          {awards.map((award, index) => (
            <li key={index} className="award-item">{award}</li>
          ))}
        </ul>
      </div>
      <div className="courses-container">
        {courses.map((course, index) => (
          <span key={index} className="course-tag">{course}</span>
        ))}
      </div>
    </div>
  );
}

function ProjectCard({ title, description, imageUrl, projectUrl }) {
  const isInternalLink = projectUrl.startsWith('/');
  
  if (isInternalLink) {
    return (
      <a href={projectUrl} className="project-card">
        <div className="project-image">
          <img src={imageUrl} alt={title} />
        </div>
        <div className="project-content">
          <h3 className="project-title">{title}</h3>
          <p className="project-description">{description}</p>
        </div>
      </a>
    );
  }
  
  return (
    <a href={projectUrl} className="project-card" target="_blank" rel="noopener noreferrer">
      <div className="project-image">
        <img src={imageUrl} alt={title} />
      </div>
      <div className="project-content">
        <h3 className="project-title">{title}</h3>
        <p className="project-description">{description}</p>
      </div>
    </a>
  );
}

function LinkCard({ title, url, icon, description }) {
  return (
    <a href={url} className="link-card" target="_blank" rel="noopener noreferrer">
      <div className="link-content">
        <div className="link-icon">
          <img 
            src={icon} 
            alt={`${title} icon`} 
            style={{
              width: '32px',
              height: '32px',
              objectFit: 'contain'
            }}
          />
        </div>
        <div className="link-text">
          <h3 className="link-title">{title}</h3>
          <p className="link-description">{description}</p>
        </div>
      </div>
    </a>
  );
}

function MainContent() {
  const navigate = useNavigate();
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains('dark-mode');
    const savedTheme = localStorage.getItem('theme');
  
    if (savedTheme) {
      if (savedTheme === 'dark' && !isDarkMode) {
        document.documentElement.classList.add('dark-mode');
      } else if (savedTheme === 'light' && isDarkMode) {
        document.documentElement.classList.remove('dark-mode');
      }
    }
  }, []);

  const toggleImage = () => {
    setIsImageExpanded(!isImageExpanded);
  };

  const toggleTheme = () => {
    const isDarkMode = document.documentElement.classList.toggle('dark-mode');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  };

  const handleClick = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const experiences = [
    {
      title: "AI Software Engineer Intern",
      company: "Chamberlain Group",
      period: "May 2025 - August 2025",
      description: "Returning to Chamberlain Group, I again worked on MyQ Enterprise, but this time I led the development of an agentic AI scheduling service within the platform. I began implementing a system that automatically schedules appointments by analyzing emails using NLP and making decisions based on their intent.",
      bulletPoints: [
        "Pioneered the company’s first NLP initiative by designing a modular prompt tree to interpret diverse user intents for scheduling.",
        "Boosted system performance by parallelizing prompt execution and dynamically structuring prompts, shortening request latency and reducing token usage.",
        "Integrated Microsoft Graph Webhooks into the existing .NET codebase to enable real-time event processing and trigger end-to-end scheduling workflows.",
        "Deployed AWS endpoints via API Gateway and Lambda to support rapid prototyping and validate third-party integrations.",
      ],
      skills: ["AWS Step Functions", "Microsoft Graph", ".NET", "C#", "SQL", "AWS Lambda", "Postman", "Prompt Chaining", "NLP", "Swagger"],
      logoUrl: "/static/images/home/chamberlain.jpg"
    },
    {
      title: "Launchpad Internship Program",
      company: "Blackstone",
      period: "June 2025 - August 2025",
      description: "Career-building program offering professional development, and networking through Blackstone and its partners.",
      skills: ["Networking", "Entrepreneurship", "Leadership", "Professional Development"],
      logoUrl: "/static/images/home/blackstone.png"
    },
    {
      title: "Software Engineer Intern",
      company: "Chamberlain Group",
      period: "May 2024 - August 2024",
      description: "At Chamberlain Group, I contributed to the MyQ Enterprise Service, a platform offering smart dock management solutions for warehousing and logistics businesses. My primary focus was maintaining uptime during a migration to a new Cloud Service Provider, automating testing processes, and monitoring the system's performance under load.",
      bulletPoints: [
        "Supported application migration during transition to a new Cloud Service Provider, maintaining 100% uptime and consistent performance.",
        "Improved deployment stability by optimizing CI/CD pipelines using Docker and Git to reduce application latency.",
        "Integrated Karate with Gatling to automate performance testing, enabling scalable and modular API test workflows.",
        "Developed end-to-end test suites using BDD and TDD practices with Karate, Selenium, and TestRail, enhancing QA coverage, traceability, and regression reliability across smoke and sanity testing."
      ],
      skills: ["Java", "Javascript", "Postman", "Swagger", "Docker", "Karate", "Selenium", "Gatling", "Scala"],
      logoUrl: "/static/images/home/chamberlain.jpg"
    },
  ];

  const education = [
    {
      degree: "Bachelor of Science in Software Engineering",
      school: "Iowa State University",
      period: "Graduating May 2026",
      description: "Dean's List: Fall 22', Spring 23', Fall 23', Spring 24', Fall 24', Spring 25'\nSoftware Engineering Student Merit Scholar\nFrederick Bucholz Merit Scholarship Recipient",
      courses: [
        "Data Structures",
        "Algorithm Design & Analysis",
        "Object Oriented Programming",
        "Web and Mobile Development",
        "Database Management",
        "Discrete Math",
        "Software Architecture",
        "Software Testing",
        "Machine Learning",
      ],
      logoUrl: "/static/images/home/swe_img.png"
    },
    {
      degree: "Minor in Cybersecurity Engineering",
      school: "Iowa State University",
      period: "Graduating May 2026",
      description: "Computer and Electrical Engineering Academic Scholarship Recipient\nTCB Council Scholarship Recipient",
      courses: [
        "Operating Systems",
        "Ethical Hacking",
        "Network Monitoring",
        "Cryptography",
        "Cloud Security",
        "Blockchain and Cryptocurrency",
      ],
      logoUrl: "/static/images/home/ece_img.jpg"
    }
  ];

  const projects = [
    {
      title: "EquationLive",
      description: "ChatGPT keyboard extension with Stripe subscriptions",
      imageUrl: "/static/images/home/equationlive.png",
      projectUrl: "https://www.equationlive.com"
    },
    {
      title: "AWS Hosted Applications",
      description: "A conglomerate of applications I've built using the AWS platform",
      imageUrl: "/static/images/home/aws.png",
      projectUrl: "/not-finished"
    },
    {
      title: "Blockchain Network Monitoring Dashboard",
      description: "Interactive dashboard for monitoring blockchain networks",
      imageUrl: "/static/images/home/blockchain.jpg",
      projectUrl: "/not-finished"
    },
    {
      title: "Congressional Trading Analyzer",
      description: "Web-scraped congressional trading data and analyzed it to automate trade execution",
      imageUrl: "/static/images/home/stockact.png",
      projectUrl: "/not-finished"
    },
    {
      title: "Recipe Sharing App",
      description: "Worked with a team as the sole backend developer for a recipe sharing app",
      imageUrl: "/static/images/home/pantrypal.png",
      projectUrl: "/not-finished"
    },
    {
      title: "eCommerce Open Source Contributions",
      description: "Applied SOLID principles to refactor the codebase in C# with the .NET framework",
      imageUrl: "/static/images/home/nopcommerce.png",
      projectUrl: "/not-finished"
    },
  ];

  const links = [
    {
      title: "LinkedIn",
      url: "https://www.linkedin.com/in/robert-study-8a6180246/",
      icon: "/static/images/home/linkedin.png",
      description: "Connect with me professionally"
    },
    {
      title: "GitHub",
      url: "https://github.com/robertstudy",
      icon: "/static/images/home/github.png",
      description: "Check out my code repositories"
    },
    {
      title: "Email",
      url: "mailto:robertstudy26@gmail.com",
      icon: "/static/images/home/email.png",
      description: "Get in touch with me"
    }
  ];

  return (
    <div className="App">
      <nav className="main-nav">
        <div className="nav-left">
          <div className="headshot-container">
            <img 
              src="/static/images/home/profile-picture.png" 
              alt="Profile" 
              className="headshot" 
              onClick={toggleImage}
            />
          </div>
          <div className="nav-logo">Robert Study</div>
        </div>
        <div className="nav-links">
          <a href="#experience" onClick={(e) => handleClick(e, 'experience')}>Work Experience</a>
          <a href="#education" onClick={(e) => handleClick(e, 'education')}>Education & Skills</a>
          <a href="#projects" onClick={(e) => handleClick(e, 'projects')}>My Projects</a>
          <a href="#contact" onClick={(e) => handleClick(e, 'links')}>Get In Touch</a>
        </div>
      </nav>
      
      <section className="hero-section">
        <div className="hero-background">
        </div>
        <div className="hero-content">
          <div className="hero-text-content">
            <h1>Software & Cybersecurity <span className="highlight">Engineering</span></h1>
            <p className="hero-text">I'm a Software Engineer with a strong foundation in Cybersecurity and AI, focused on building scalable, secure cloud 
              applications. My experience ranges from individual projects to enterprise-scale solutions, with reliability and security always in mind. I work 
              well independently but also thrive in team environments especially when I can find ways to communicate complex ideas to both technical and non-technical 
              audiences.</p>
            <div className="cta-group">
              <button className="cta-button" onClick={() => navigate('/about')}>More About Me</button>
              <button className="cta-button" onClick={() => {
                const link = document.createElement('a');
                link.href = '/static/files/Robert_Study_Resume.pdf';
                link.download = 'Robert_Study_Resume.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}>View My Resume</button>
            </div>
          </div>
        </div>
      </section>

      <section id="experience" className="experience-section">
        <div className="section-container">
          <h2>Experience</h2>
          <div className="experience-grid">
            {experiences.map((exp, index) => (
              <ExperienceCard key={index} {...exp} />
            ))}
          </div>
        </div>
      </section>

      <section id="education" className="education-section">
        <div className="section-container">
          <h2>Education</h2>
          <div className="education-grid">
            {education.map((edu, index) => (
              <EducationCard key={index} {...edu} />
            ))}
          </div>
        </div>
      </section>

      <section id="projects" className="projects-section">
        <div className="section-container">
          <h2>Projects</h2>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <ProjectCard key={index} {...project} />
            ))}
          </div>
        </div>
      </section>

      <section id="links" className="links-section">
        <div className="section-container">
          <h2>Connect With Me</h2>
          <div className="links-grid">
            {links.map((link, index) => (
              <LinkCard key={index} {...link} />
            ))}
          </div>
        </div>
      </section>

      <footer style={{
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-color)',
        padding: '1.5rem',
        textAlign: 'center',
        borderTop: '1px solid var(--accent-color)',
        marginTop: '2rem'
      }}>
        <p style={{ 
          fontSize: '0.9rem',
          opacity: '0.8'
        }}>
          © {new Date().getFullYear()} Robert Study. All rights reserved.
        </p>
      </footer>

      <button onClick={toggleTheme} className="theme-toggle">
        <span className="light-icon">Light Mode</span>
        <span className="dark-icon">Dark Mode</span>
      </button>

      {isImageExpanded && (
        <div className="modal-overlay" onClick={toggleImage}>
          <div className="modal-content">
            <img 
              src="/static/images/home/profile-picture.png"
              alt="Profile" 
              className="expanded-profile-pic"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollRestoration>
        <Routes>
          <Route path="/" element={<MainContent />} />
          <Route path="/about" element={<AboutMe />} />
          <Route path="/not-finished" element={<NotFinished />} />
          <Route path="/aws-applications" element={<AwsApplications />} />
          <Route path="/pantrypal" element={<PantryPal />} />
        </Routes>
      </ScrollRestoration>
    </Router>
  );
}

export default App;
